import { FILTER_TYPES } from "services/search/const";
import config from "config";

/**
 * Client-side search adapter.
 * Reads structured search data from a JSON script tag and matches it
 * against DOM elements for visibility toggling. Suitable for small,
 * server-rendered datasets such as event listings.
 *
 * Configuration (via sal-config JSON):
 *   backend.type: "client"
 *   backend.client.itemSelector: CSS selector for filterable DOM items (default: "[data-event-id]")
 *   backend.client.dataScriptId: id of the <script type="application/json"> element (default: "veranstaltungen-event-search-data")
 */
export function createClientAdapter(backendConfig) {
  const clientConfig = backendConfig.client || {};
  const itemSelector = clientConfig.itemSelector;
  const dataScriptId = clientConfig.dataScriptId;

  let allItems = null;

  return {
    fetchResult(searchOptions) {
      if (!allItems) {
        allItems = buildItems(itemSelector, dataScriptId);
      }

      const { filters } = searchOptions;
      const { activeFilters, queryTerm } = buildActiveFilters(filters);

      const filteredItems = allItems.filter(
        (item) =>
          matchesAllFilters(item, activeFilters) &&
          matchesQuery(item, queryTerm),
      );
      const visibleItems = expandRelatedItems(filteredItems);

      applyDomVisibility(allItems, visibleItems);
      const facets = buildFacets(allItems, activeFilters, queryTerm);

      return Promise.resolve({
        records: visibleItems.map((item) => item.data),
        count: visibleItems.length,
        facets,
        facetpaths: {},
      });
    },

    fetchSuggestions() {
      return Promise.resolve([]);
    },
  };
}

function expandRelatedItems(items) {
  const expandedItems = new Set();
  items.forEach((item) => {
    if (item.relatedItems?.length) {
      item.relatedItems.forEach((relatedItem) => {
        expandedItems.add(relatedItem);
      });
      return;
    }
    if (item.element) {
      expandedItems.add(item);
    }
  });
  return Array.from(expandedItems);
}

/**
 * Reads search data from a JSON script tag and pairs each entry with
 * the corresponding DOM element via the veranstaltungId field.
 * Event series are linked to their event items via their shortlinks.
 */
function buildItems(itemSelector, scriptId) {
  const scriptEl = document.getElementById(scriptId);
  const searchDataList = scriptEl ? JSON.parse(scriptEl.textContent) : [];
  const elements = Array.from(document.querySelectorAll(itemSelector));

  let eventElementIndex = 0;

  const items = searchDataList.map((data) => {
    const isEventSeries = data.relatedEventShortlinks?.length > 0;

    return {
      element: isEventSeries ? null : elements[eventElementIndex++] || null,
      data,
    };
  });

  const eventItemsByShortlink = new Map(
    items
      .filter((item) => item.element && item.data.shortlink)
      .map((item) => [item.data.shortlink, item]),
  );

  items.forEach((item) => {
    if (!item.data.relatedEventShortlinks?.length) return;

    item.relatedItems = item.data.relatedEventShortlinks.flatMap(
      (shortlink) => eventItemsByShortlink.get(shortlink) ?? [],
    );
  });

  return items;
}

/**
 * Extracts active filter values and query term from the Redux filter state.
 */
function buildActiveFilters(filters) {
  const active = {};
  let queryTerm = "";
  const filterDefs = config.filters || {};

  Object.keys(filters).forEach((filterName) => {
    const filter = filters[filterName];
    const filterDef = filterDefs[filterName];

    if (!filter || !filter.values || filter.values.length === 0 || !filterDef) {
      return;
    }

    if (filterDef.type === FILTER_TYPES.QUERY) {
      queryTerm = (filter.values[0] || "").trim();
      return;
    }

    active[filterName] = filter.values;
  });

  return { queryTerm, activeFilters: active };
}

/**
 * Tests whether an item matches all active filters.
 * AND logic between different filter types, OR within the same filter.
 * Values in the search data can be scalars or arrays.
 */
function matchesAllFilters(item, activeFilters) {
  for (const filterName in activeFilters) {
    const selectedValues = activeFilters[filterName];
    const itemValue = getItemValues(item, filterName);

    if (itemValue.length === 0) return false;

    const hasMatch = selectedValues.some((selected) =>
      itemValue.includes(selected),
    );
    if (!hasMatch) return false;
  }
  return true;
}

/**
 * Tests whether an item matches a free-text query.
 * Searches across shortlink, titel, beschreibung, detailTitles, detailDescriptions, speakers.
 */
function matchesQuery(item, queryTerm) {
  if (!queryTerm) return true;
  const query = queryTerm.toLowerCase();
  const data = item.data;
  const textFields = [
    data.shortlink,
    data.titel,
    data.beschreibung,
    ...(Array.isArray(data.detailTitles) ? data.detailTitles : []),
    ...(Array.isArray(data.detailDescriptions) ? data.detailDescriptions : []),
    ...(Array.isArray(data.speakers) ? data.speakers : []),
  ];
  return textFields.some(
    (field) => field && String(field).toLowerCase().includes(query),
  );
}

/**
 * Extracts filter values from an item's search data as a flat array.
 */
function getItemValues(item, filterName) {
  const data = item.data[filterName];

  if (data == null) return [];
  if (Array.isArray(data)) return data.map((v) => String(v));
  return [String(data)];
}

/**
 * Builds facet counts for each filter from the full item set.
 * Counts are computed against items matching all OTHER active filters
 * (cross-filter counting).
 */
function buildFacets(allItems, activeFilters, queryTerm) {
  const facets = {};
  const filterDefs = config.filters || {};

  Object.keys(filterDefs).forEach((filterName) => {
    const def = filterDefs[filterName];
    if (def.type === FILTER_TYPES.QUERY || def.type === FILTER_TYPES.FACET_PATH)
      return;

    // Items matching all OTHER filters (cross-filter)
    const otherFilters = { ...activeFilters };
    delete otherFilters[filterName];
    const itemsForFacet = allItems.filter(
      (item) =>
        matchesAllFilters(item, otherFilters) && matchesQuery(item, queryTerm),
    );

    const valueCounts = {};
    itemsForFacet.forEach((item) => {
      const values = getItemValues(item, filterName);
      values.forEach((v) => {
        valueCounts[v] = (valueCounts[v] || 0) + 1;
      });
    });

    const fieldName = def.field;
    facets[fieldName] = Object.keys(valueCounts)
      .sort()
      .map((value) => ({
        label: value,
        value,
        count: valueCounts[value],
      }));
  });

  return facets;
}

/**
 * Shows/hides DOM elements based on filter results.
 */
function applyDomVisibility(allItems, visibleItems) {
  const visibleSet = new Set(visibleItems);

  allItems.forEach((item) => {
    if (!item.element) return;

    item.element.style.display = visibleSet.has(item) ? "" : "none";
  });
}
